import baustein from "baustein";
import { BREAKPOINTS } from "web/react/constants";
import analytics from "web/script/analytics/analytics";
import globals from "web/script/modules/globals";
import { getLanguageWithoutCountry } from "web/script/modules/language";
import { addVisibilityWatcher } from "web/script/modules/visibility-watcher";
import googleCsa from "web/script/embedded/google-adsense";
import browser from "web/script/utils/browser";

const channelIdMap = {
    "oos-pdp": { mobile: "1598572231", desktop: "8791081831" },
    "instock-pdp": { mobile: "8526409836", desktop: "7049676638" },
    "clp-top": { mobile: "8286600639", desktop: "6809867433" },
    "clp-end": { mobile: "8718859835", desktop: "7242126631" },
    "clp-top-both": { mobile: "8286600639+6754983760", desktop: "6809867433+6754983760" },
    "clp-end-both": { mobile: "8718859835+6754983760", desktop: "7242126631+6754983760" },
};

const shoppingChannelIdMap = {
    "instock-pdp": {
        "lyst.co.uk": "pla-ec-uk",
        "lyst.com": "pla-ec-us",
    },
    "oos-pdp": {
        "lyst.co.uk": "pdp-oos-uk",
        "lyst.com": "pdp-oos-com",
    },
    feed: {
        "lyst.ca": "feed-ca",
        "lyst.com.au": "feed-au",
        "lyst.it": "feed-it",
        "lyst.co.uk": "feed-uk-paid",
    },
};

const shoppingUnitMap = {
    "feed-classic": {
        mobile: { styleId: "6877125116", numberOfProducts: 11 },
        tablet: { styleId: "4396941166", numberOfProducts: 3 },
        bigtablet: { styleId: "6457866956", numberOfProducts: 3 },
        netbook: { styleId: "6775628563", numberOfProducts: 4 },
        desktop: { styleId: "2605215193", numberOfProducts: 4 },
    },
    "feed-2020": {
        mobile: { styleId: "6493972874", numberOfProducts: 11 },
        tablet: { styleId: "8443912564", numberOfProducts: 3 },
        bigtablet: { styleId: "2062292250", numberOfProducts: 3 },
        netbook: { styleId: "3067548115", numberOfProducts: 4 },
        desktop: { styleId: "6244568227", numberOfProducts: 4 },
    },
    "instock-pdp-classic": {
        mobile: { styleId: "4342967516", numberOfProducts: 11 },
        tablet: { styleId: "4342967516", numberOfProducts: 4 },
        desktop: { styleId: "2762612834", numberOfProducts: 5 },
    },
    "oos-pdp-classic": {
        mobile: { styleId: "4342967516", numberOfProducts: 11 },
        tablet: { styleId: "4342967516", numberOfProducts: 4 },
        desktop: { styleId: "2762612834", numberOfProducts: 5 },
    },
    "instock-pdp-2020": {
        mobile: { styleId: "1355743730", numberOfProducts: 11 },
        tablet: { styleId: "1355743730", numberOfProducts: 4 },
        desktop: { styleId: "5047513430", numberOfProducts: 5 },
    },
    "oos-pdp-2020": {
        mobile: { styleId: "1355743730", numberOfProducts: 11 },
        tablet: { styleId: "1355743730", numberOfProducts: 4 },
        desktop: { styleId: "5047513430", numberOfProducts: 5 },
    },
};

export default baustein.register("adsense-custom-search-ads", {
    init() {
        googleCsa();
    },

    onInsert() {
        // modules with 'position' set are triggered explicitly
        if (!this.options.dataPosition) {
            this._removeVisiblityWatcher = addVisibilityWatcher(this.el, (visible) => {
                if (visible) {
                    if (this.el.querySelector(".adsense-shopping-container")) {
                        this.createCsaShoppingUnit();
                    } else {
                        this.createCsaUnit();
                    }

                    this._removeVisiblityWatcher();
                }
            });
        }
    },

    createCsaUnit() {
        const csaContainer = this.el.querySelector(".adsense-container");
        const { keyword, pagetype } = this.options;

        const channelId = getFromDeviceMap(channelIdMap, pagetype);

        const pageOptions = {
            pubId: "pub-6862407432380262",
            query: keyword,
            adPage: 1,
            location: false,
            siteLinks: showSitelinks(pagetype),
            channel: channelId,
        };

        const adblock1 = {
            container: csaContainer.id,
            width: "100%",
            maxTop: 2,
            fontFamily: "helvetica neue",
            colorTitleLink: "021135",
            colorDomainLink: "6D7288",
            colorText: "021135",
            adLoadedCallback: function(containerName, adsLoaded) {
                if (adsLoaded) {
                    try {
                        onAdLoaded(csaContainer, "adsense", channelId);
                    } catch (err) {
                        analytics.event("adsense", "no_ad", channelId, true);
                    }
                }
            },
        };

        googleCsa("ads", pageOptions, adblock1);
        analytics.event("adsense", "impression", channelId, true);
    },

    createCsaShoppingUnit() {
        const csaContainer = this.el.querySelector(".adsense-shopping-container");
        const { domain, keyword, pagetype } = this.options;

        const channel = pagetype.replace("-classic", "").replace("-2020", "");
        const channelId = getFromDomainMap(shoppingChannelIdMap, channel, domain);
        const { styleId, numberOfProducts } = getFromDeviceMap(shoppingUnitMap, pagetype);

        const pageOptions = {
            pubId: "partner-vert-pla-lyst-srp",
            styleId: styleId,
            query: keyword,
            personalizedAds: false,
            channel: channelId,
            hl: getLanguageWithoutCountry(),
            adtest: isPreviewDomain() ? "on" : "off",
        };

        const afshBlock = {
            container: csaContainer.id,
            width: csaContainer.offsetWidth,
            number: numberOfProducts,
            enableInteractive: true,
            linkTarget: "_blank",
            adLoadedCallback: function(containerName, adsLoaded) {
                if (adsLoaded) {
                    try {
                        onAdLoaded(csaContainer, "adsense-shopping", channelId);
                    } catch (err) {
                        analytics.event("adsense-shopping", "no_ad", channelId, true);
                    }
                }
            },
        };

        googleCsa("plas", pageOptions, afshBlock);
        analytics.event("adsense-shopping", "impression", channelId, true);
    },
});

function isPreviewDomain() {
    return globals.window.location.hostname.split(".").indexOf("lystpreviews") > 0;
}

function getFromDomainMap(map, pagetype, domain) {
    const domainMap = map[pagetype];
    if (domain in domainMap) {
        return domainMap[domain];
    }
}

export function getFromDeviceMap(map, pagetype) {
    const deviceMap = map[pagetype];
    const windowWidth = browser.getClientWidth();

    // NB. not BREAKPOINTS.ExtraLarge, a bit bigger than that (1200px)
    if (windowWidth >= 1305) {
        // optional desktop breakpoint
        return deviceMap["desktop"];
        // NB. not BREAKPOINTS.Large, a tiny big bigger (992px)
    } else if (windowWidth >= 1021) {
        // optional netbook breakpoint - defaults to desktop
        if ("netbook" in deviceMap) {
            return deviceMap["netbook"];
        }
        return deviceMap["desktop"];
    } else if (windowWidth >= 992) {
        // optional breakpoint - defaults to desktop
        if ("bigtablet" in deviceMap) {
            return deviceMap["bigtablet"];
        }
        if ("tablet" in deviceMap) {
            return deviceMap["tablet"];
        }
        return deviceMap["desktop"];
    } else if (windowWidth >= BREAKPOINTS.Medium) {
        // optional tablet breakpoint - defaults to desktop
        if ("tablet" in deviceMap) {
            return deviceMap["tablet"];
        }
        return deviceMap["desktop"];
    } else {
        // all maps should support mobile
        return deviceMap["mobile"];
    }
}

function showSitelinks(pagetype) {
    if (pagetype && pagetype === "instock-pdp") {
        return true;
    }
    return false;
}

function onAdLoaded(csaContainer, eventCategory, eventLabel) {
    let csaIframeChildren = csaContainer.children;
    let mouseClick = false;
    let touchClick = false;
    let category = eventCategory;
    let label = eventLabel;

    function mouseEnter() {
        mouseClick = true;
    }

    function mouseLeave() {
        mouseClick = false;
    }

    function touchStart() {
        touchClick = true;
    }

    function touchMove() {
        touchClick = false;
    }

    function estimatedClick() {
        if (mouseClick || touchClick) {
            // remove all listeners once guesstimate click event occurs
            var csaIframe;
            for (var index = 0; index < csaIframeChildren.length; index++) {
                csaIframe = csaIframeChildren[index];
                csaIframe.removeEventListener("mouseenter", mouseEnter);
                csaIframe.removeEventListener("mouseleave", mouseLeave);
                csaIframe.removeEventListener("touchstart", touchStart);
                csaIframe.removeEventListener("touchmove", touchMove);
            }

            window.removeEventListener("blur", estimatedClick);

            analytics.event(category, "click", label);
        }
    }

    // add guesstimate click listeners
    var csaIframe;
    for (var index = 0; index < csaIframeChildren.length; index++) {
        csaIframe = csaIframeChildren[index];
        csaIframe.addEventListener("mouseenter", mouseEnter);
        csaIframe.addEventListener("mouseleave", mouseLeave);
        csaIframe.addEventListener("touchstart", touchStart);
        csaIframe.addEventListener("touchmove", touchMove);
    }

    window.addEventListener("blur", estimatedClick);
}
